import React from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Plus, Tent } from "lucide-react";
import type { ActivityTripRef } from "@atlas/shared";
import { fetchTrips } from "../api/trips";
import { useTripGrouping } from "../hooks/useTripGrouping";
import { formatTripDates, plural } from "../lib/trip-format";

// Spec 35: "Part of {trip}" on an activity screen, or "Add to a trip" when it
// is in none. Adding goes through useTripGrouping, so the error copy matches
// the Activities page.

export function ActivityTripLink({ activityId, trip }: { activityId: string; trip: ActivityTripRef | null | undefined }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const grouping = useTripGrouping({
    onDone: () => {
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    },
  });
  const trips = useQuery({ queryKey: ["trips"], queryFn: fetchTrips, enabled: open && !trip });

  if (trip) {
    return (
      <Link
        to="/trips/$tripId"
        params={{ tripId: trip.id }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px 6px 8px",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-subtle)",
          background: "var(--surface-raised)",
          color: "var(--text-secondary)",
          fontSize: 13,
          textDecoration: "none",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <Tent size={15} style={{ flexShrink: 0, color: "var(--text-muted)" }} />
        <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          Part of <strong style={{ color: "var(--text-primary)", fontWeight: 600 }}>{trip.name}</strong>
        </span>
        <ChevronRight size={14} style={{ flexShrink: 0, color: "var(--text-muted)" }} />
      </Link>
    );
  }

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px",
          borderRadius: "var(--radius-md)",
          border: "1px dashed var(--border-subtle)",
          background: "transparent",
          color: "var(--text-secondary)",
          fontFamily: "var(--font-sans)",
          fontSize: 13,
          cursor: "pointer",
        }}
      >
        <Plus size={14} /> Add to a trip
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 20,
            width: 300,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: 320,
            overflowY: "auto",
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-md)",
            padding: 6,
          }}
        >
          {trips.isLoading ? (
            <div style={{ padding: 10, fontSize: 13, color: "var(--text-muted)" }}>Loading your trips…</div>
          ) : trips.isError ? (
            <div style={{ padding: 10, fontSize: 13, color: "var(--status-danger)" }}>Couldn't load your trips.</div>
          ) : (trips.data?.trips.length ?? 0) === 0 ? (
            <div style={{ padding: 10, fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
              No trips yet. Select two or more activities on the Activities page to make one.
            </div>
          ) : (
            trips.data!.trips.map((t) => (
              <button
                key={t.id}
                type="button"
                role="menuitem"
                disabled={grouping.pending}
                onClick={() => grouping.addToTrip(t.id, [activityId])}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: 2,
                  width: "100%",
                  padding: "8px 10px",
                  border: "none",
                  borderRadius: "var(--radius-md)",
                  background: "transparent",
                  color: "var(--text-primary)",
                  fontFamily: "var(--font-sans)",
                  textAlign: "left",
                  cursor: grouping.pending ? "wait" : "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-raised)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <span style={{ fontSize: 14, fontWeight: 600 }}>{t.name}</span>
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {formatTripDates(t.startedAt, t.endedAt)} · {plural(t.activityCount, "activity", "activities")}
                </span>
              </button>
            ))
          )}
          {grouping.error && (
            <div style={{ padding: "6px 10px", fontSize: 12.5, color: "var(--status-danger)" }}>{grouping.error}</div>
          )}
        </div>
      )}
      {grouping.confirmElement}
    </div>
  );
}
